import { useState, useEffect, RefObject } from 'react';

/**
 * Reference width matching backend's CARD_REFERENCE_WIDTH.
 * All overlay pixel values are authored for this width.
 * When the container is wider/narrower, we scale proportionally.
 */
export const OVERLAY_REFERENCE_WIDTH = 360;

/**
 * Base pixel values at reference width (360px).
 * These match the backend overlay.html template values before scaling.
 */
export const OVERLAY_CONSTANTS = {
  REFERENCE_WIDTH: 360,
  PADDING: 16,         // px-4 in Tailwind
  GAP: 8,              // gap-2 in Tailwind
  MARGIN_BOTTOM: 6,    // mb-1.5 — tightened so text elements read together
  LOGO_HEIGHT: 32,     // h-8 in Tailwind
  LOGO_ICON_MAX: 48,
  LOGO_WIDE_MAX: 120,
  LOGO_RADIUS: 10,
  BRAND_FONT: 18,
  FALLBACK_RADIUS: 8,
  CATEGORY_FONT: 10,
  CATEGORY_PAD_V: 2,
  CATEGORY_PAD_H: 8,
  CATEGORY_RADIUS: 4,
  TEXT_PADDING: 4,
  TITLE_BG_RADIUS: 4,
  SUBHEADER_BG_RADIUS: 4,
};

/**
 * Hook that calculates a scale factor based on container width.
 * Uses ResizeObserver to automatically update when container resizes.
 *
 * @param containerRef - Ref to the overlay container element
 * @returns Scale factor (containerWidth / REFERENCE_WIDTH)
 */
export function useOverlayScale(containerRef: RefObject<HTMLElement>): number {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateScale = () => {
      const width = container.getBoundingClientRect().width;
      if (width > 0) {
        setScale(width / OVERLAY_REFERENCE_WIDTH);
      }
    };

    // Initial calculation
    updateScale();

    // Watch for resize
    const observer = new ResizeObserver(updateScale);
    observer.observe(container);

    return () => observer.disconnect();
  }, [containerRef]);

  return scale;
}

/**
 * Generates scaled style values for overlay elements.
 * Call this with the scale factor and font sizes to get properly scaled pixel values.
 */
export function getScaledOverlayStyles(
  scale: number,
  titleFontSize: number,
  subheaderFontSize: number
) {
  return {
    // Container padding
    padding: Math.round(OVERLAY_CONSTANTS.PADDING * scale),

    // Spacing
    gap: Math.round(OVERLAY_CONSTANTS.GAP * scale),
    marginBottom: Math.round(OVERLAY_CONSTANTS.MARGIN_BOTTOM * scale),

    // Logo
    logoHeight: Math.round(OVERLAY_CONSTANTS.LOGO_HEIGHT * scale),
    logoIconMax: Math.round(OVERLAY_CONSTANTS.LOGO_ICON_MAX * scale),
    logoWideMax: Math.round(OVERLAY_CONSTANTS.LOGO_WIDE_MAX * scale),
    logoRadius: Math.round(OVERLAY_CONSTANTS.LOGO_RADIUS * scale),

    // Brand text
    brandFont: Math.round(OVERLAY_CONSTANTS.BRAND_FONT * scale),
    fallbackRadius: Math.round(OVERLAY_CONSTANTS.FALLBACK_RADIUS * scale),

    // Category badge
    categoryFont: Math.round(OVERLAY_CONSTANTS.CATEGORY_FONT * scale),
    categoryPadV: Math.round(OVERLAY_CONSTANTS.CATEGORY_PAD_V * scale),
    categoryPadH: Math.round(OVERLAY_CONSTANTS.CATEGORY_PAD_H * scale),
    categoryRadius: Math.round(OVERLAY_CONSTANTS.CATEGORY_RADIUS * scale),

    // Text padding
    textPadding: Math.round(OVERLAY_CONSTANTS.TEXT_PADDING * scale),

    // Fonts (user-configurable, but must be scaled)
    titleFont: titleFontSize * scale,          // unrounded: rounding made the card's type up to 1 % smaller than the render and wraps differed
    subheaderFont: subheaderFontSize * scale,

    // Background radius
    titleBgRadius: Math.round(OVERLAY_CONSTANTS.TITLE_BG_RADIUS * scale),
    subheaderBgRadius: Math.round(OVERLAY_CONSTANTS.SUBHEADER_BG_RADIUS * scale),
  };
}

export default useOverlayScale;
